<div class="product-image-manager">
  @if (isSearchOpen) {
      <!-- Search a provider for a picture instead of uploading one -->
      <app-image-search-panel
        subject="product"
        [entityId]="productId"
        primaryActionLabel="Use as primary"
        (imported)="onImageImported()"
        (cancelled)="closeSearch()"/>
  } @else {
    <!-- Actions first: in the stacked (mobile / narrow modal) layout the preview
       is 400px tall, so anything below it starts off-screen. -->
    <div class="mb-4 flex flex-col sm:flex-row gap-2">
      <ui-button
        variant="primary"
        size="md"
        [fullWidth]="true"
        [disabled]="isUploading"
        [class.upload-drop-zone]="true"
        [class.dragging-over]="isDraggingOver"
        (clicked)="uploadImage()"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)">
        <fa-icon [icon]="isUploading ? faSpinner : faUpload" [animation]="isUploading ? 'spin' : undefined"
                 class="mr-2"></fa-icon>
        {{ isUploading ? 'Uploading...' : isDraggingOver ? 'Drop image here' : 'Add new Image' }}
      </ui-button>
      <ui-button
        variant="secondary"
        size="md"
        [fullWidth]="true"
        [disabled]="isUploading"
        (clicked)="openSearch()">
        <fa-icon [icon]="faSearch" class="mr-2"></fa-icon>
        Search image
      </ui-button>
      </div>

  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
    <!-- Left side: Preview -->
    @if (images.length > 0) {
      <div class="preview-section">
        <div class="preview-container">
          <img
            [src]="selectedImage?.url || images[0]?.url"
            class="preview-image"
            alt="Product image preview"/>
          <!-- Provider Badge/Logo (Top Left) -->
          <div class="provider-badge">
            @if (isUserOwned(selectedImage || images[0])) {
              <span class="badge badge-primary badge-sm">
                User
              </span>
            } @else if (isSearchOwned(selectedImage || images[0])) {
              <span class="badge badge-accent badge-sm">
                Web
              </span>
            } @else {
              <img
                [src]="getProviderLogoPath((selectedImage || images[0])?.provider) | async"
                class="provider-logo"
                alt="Provider logo"/>
            }
          </div>
          <span class="preview-type-badge"
                [class.badge-thumbnail]="(selectedImage || images[0])?.type === 'thumbnail'"
                [class.badge-full]="(selectedImage || images[0])?.type === 'full'">
            {{ (selectedImage || images[0])?.type === 'thumbnail' ? 'Thumbnail' : 'Full' }}
          </span>

          <!-- Primary action area -->
          <div class="primary-action">
            @if ((selectedImage || images[0])?.is_primary) {
              <!-- Glass indicator when already primary -->
              <div class="primary-indicator">
                <fa-icon [icon]="faCheck" class="mr-2"></fa-icon>
                Primary {{ (selectedImage || images[0])?.type }}
              </div>
            } @else {
              <!-- Button to make primary -->
              <ui-button
                variant="secondary"
                size="md"
                [fullWidth]="true"
                (clicked)="makePrimary()">
                Use as Primary {{ (selectedImage || images[0])?.type }}
              </ui-button>
            }
          </div>
        </div>
        <!-- Credit line — mandatory for licences like Open Food Facts' CC-BY-SA -->
        @if (attributionFor(selectedImage || images[0]); as credit) {
          <p class="attribution-line">via {{ credit }}</p>
        }
        <div class="mt-3 flex gap-2 justify-center flex-wrap">
          <!-- Cuts the product out of its background and stores the result as a
               new photo — the source image is kept either way. -->
          @if (canEnhance(selectedImage || images[0])) {
            <ui-button
              variant="accent"
              size="sm"
              [disabled]="isEnhancing"
              (clicked)="enhanceImage()">
              <fa-icon [icon]="isEnhancing ? faSpinner : faWand"
                       [animation]="isEnhancing ? 'spin' : undefined"
                       class="mr-1"></fa-icon>
              {{ isEnhancing ? 'Removing background…' : 'Remove background' }}
            </ui-button>
          }
          @if (isUserManaged(selectedImage || images[0])) {
            <ui-button
              variant="danger"
              size="sm"
              [disabled]="isEnhancing"
              (clicked)="deleteImage()">
              <fa-icon [icon]="faTrash" class="mr-1"></fa-icon>
              Delete
            </ui-button>
          }
        </div>
        @if (enhanceError) {
          <div class="alert alert-error py-2 px-3 mt-2" role="alert">
            <small>
              <fa-icon [icon]="faExclamation" class="mr-1"></fa-icon>
              {{ enhanceError }}</small>
          </div>
        }
      </div>
    }

    <!-- Right side: Grid / Upload / Search -->
    <div [class]="images.length > 0 ? '' : 'md:col-span-2'">
      <!-- Empty State Message -->
      @if (images.length === 0) {
        <div class="empty-state text-center p-4">
          <fa-icon [icon]="faUpload" class="empty-state-icon mb-3"></fa-icon>
          <h5 class="text-base-content/60 font-semibold">No images yet</h5>
          <p class="text-base-content/50 text-sm">Upload your first image to get started</p>
        </div>
      }

      <!-- Full Images Section -->
      @if (fullImages.length > 0) {
        <div class="image-section">
          <h6 class="section-title">
            Full Images
            <span class="badge badge-secondary badge-sm ml-1">{{ fullImages.length }}</span>
          </h6>
          @if (!hasPrimaryFull) {
            <div class="alert alert-error py-2 px-3 mb-3" role="alert">
              <small>
                <fa-icon [icon]="faExclamation" class="mr-1"></fa-icon>
                Please select a primary full image</small>
            </div>
          }
          <div class="image-grid">
            @for (image of fullImages; track image) {
              <div
                class="grid-item"
                [class.selected]="selectedImage?.id === image.id"
                [class.primary]="image.is_primary"
                (click)="selectImage(image)">
                <img
                  [src]="image.url"
                  class="grid-image"
                  alt="Product image"/>
                <!-- Provider Badge/Logo (Top Left) -->
                <div class="grid-provider-badge">
                  @if (isUserOwned(image)) {
                    <span class="badge badge-primary badge-xs">
                      User
                    </span>
                  } @else if (isSearchOwned(image)) {
                    <span class="badge badge-accent badge-xs">
                      Web
                    </span>
                  } @else {
                    <img
                      [src]="getProviderLogoPath(image.provider) | async"
                      class="grid-provider-logo"
                      alt="Provider logo"/>
                  }
                </div>
                @if (image.is_primary) {
                  <div class="primary-banner">
                    Primary
                  </div>
                }
                @if (selectedImage?.id === image.id) {
                  <div class="selected-overlay"></div>
                }
              </div>
            }
          </div>
        </div>
      }

      <!-- Thumbnail Images Section -->
      @if (thumbnailImages.length > 0) {
        <div class="image-section">
          <h6 class="section-title">
            Thumbnails
            <span class="badge badge-secondary badge-sm ml-1">{{ thumbnailImages.length }}</span>
          </h6>
          @if (!hasPrimaryThumbnail) {
            <div class="alert alert-error py-2 px-3 mb-3" role="alert">
              <small>
                <fa-icon [icon]="faExclamation" class="mr-1"></fa-icon>
                Please select a primary thumbnail</small>
            </div>
          }
          <div class="image-grid">
            @for (image of thumbnailImages; track image) {
              <div
                class="grid-item"
                [class.selected]="selectedImage?.id === image.id"
                [class.primary]="image.is_primary"
                (click)="selectImage(image)">
                <img
                  [src]="image.url"
                  class="grid-image"
                  alt="Product image"/>
                <!-- Provider Badge/Logo (Top Left) -->
                <div class="grid-provider-badge">
                  @if (isUserOwned(image)) {
                    <span class="badge badge-primary badge-xs">
                      User
                    </span>
                  } @else if (isSearchOwned(image)) {
                    <span class="badge badge-accent badge-xs">
                      Web
                    </span>
                  } @else {
                    <img
                      [src]="getProviderLogoPath(image.provider) | async"
                      class="grid-provider-logo"
                      alt="Provider logo"/>
                  }
                </div>
                @if (image.is_primary) {
                  <div class="primary-banner">
                    Primary
                  </div>
                }
                @if (selectedImage?.id === image.id) {
                  <div class="selected-overlay"></div>
                }
              </div>
            }
          </div>
        </div>
      }
    </div>
    </div>
  }
</div>

